/**
 * OpsGroup — Thin enrollment table linking to a Dynamics Account
 *
 * A group exists in Vera Ops only when explicitly enrolled here.
 * Stores the Dynamics account GUID plus Ops-specific metadata.
 */

/**
 * Explicit, IM-controlled implementation phase for a group. Independent of
 * the group's other statuses and of whether an implementation project exists.
 */
export const ImplementationPhase = {
  PRE: 100000000,
  IN: 100000001,
  COMPLETE: 100000002,
} as const;
export type ImplementationPhaseValue = (typeof ImplementationPhase)[keyof typeof ImplementationPhase];

export const ImplementationPhaseLabels: Record<number, string> = {
  [ImplementationPhase.PRE]: 'Pre-implementation',
  [ImplementationPhase.IN]: 'In Implementation',
  [ImplementationPhase.COMPLETE]: 'Implementation Complete',
};

export interface OpsGroup {
  id: string;
  name: string;
  accountId: string;
  accountName?: string;
  accountNumber?: string;
  city?: string;
  state?: string;
  /** Account street (address1_line1). With city/state/postalCode this is the
   *  Operation Daylight CRM address check (#1304) - all four parts required. */
  street?: string;
  postalCode?: string;
  /** Account main phone (telephone1). Read live from CRM, like the address. */
  phone?: string;
  enrolledOn?: string;
  enrolledById?: string;
  enrolledByName?: string;
  /** Vera's own "is this group on the active book" shelf flag. NOT the same
   *  question as "did they term" — that is `termDate`, sourced from CRM. See
   *  groupLifecycle() in ../lib/lifecycle, which is the only thing that should
   *  be deciding what to show a user, and docs/design/group-lifecycle. */
  isActive: boolean;
  /** Why this group left, in plain words (vba_termreason). Vera's note about
   *  the CRM term date; the date itself stays in CRM. Reads undefined until
   *  the column is provisioned (schema:provision-term-reason). */
  termReason?: string;
  notes?: string;
  /** Alternate names this group is known by — AKA, DBA, FKA. Vera-maintained
   *  free text (like vba_name, the legal name). Reads undefined until the
   *  vba_othergroupnames column is provisioned. */
  otherGroupNames?: string;
  /** LinkSquare URL for the active contract document */
  contractUrl?: string;
  /** Explicit IM-controlled implementation phase (Pre / In / Complete). */
  implementationPhase?: ImplementationPhaseValue;
  /** Account Exec display name — sourced from the linked Dynamics Account's
   *  owner via $expand. (The OpsGroup row owner is a separate concept — that's
   *  just "who created this row in Vera"; not surfaced to users.) */
  ownerName?: string;
  /** Account Exec's systemuser id (the linked Dynamics account's owner id).
   *  Stable join key for Operation Daylight teams (which are 1:1 with an AE),
   *  unlike ownerName which can change. From Account._ownerid_value via $expand. */
  ownerId?: string;
  /** Historical-data-verified flag (vba_historicaldataverified). Set by the
   *  Account Exec when a group's scattered history has been fully reconciled
   *  into Vera — "this group's history is trustworthy and locked". Powers the
   *  golden-coast / lit-beam state on Operation Daylight. Defaults false (and
   *  reads false harmlessly until the column is provisioned). */
  verified: boolean;
  /** Audit: who verified this group's history, and when. Stamped by
   *  useUpdateOpsGroup on the verify transition; left in place if unverified. */
  verifiedBy?: string;
  verifiedOn?: string;
  /** Do-not-contact flag (vba_donotcontact, ADO #1594). Set when the group is
   *  managed through its broker/TPA and must never be contacted directly.
   *  Also satisfies the Operation Daylight group-contact check. Reads false
   *  harmlessly until the column is provisioned
   *  (schema:provision-do-not-contact). */
  doNotContact: boolean;
  /** Group effective date — from Account.dts_effectivedate via $expand */
  effectiveDate?: string;
  /** Group term date — from Account.dts_termdate via $expand */
  termDate?: string;
  /** Broker of record name — from Account.dts_BrokerOfRecord (contact) via $expand */
  brokerName?: string;
  /** Broker of record contactid. Stable key for comparing the BOR against
   *  brokers on opportunities (Housekeeping data-quality checks); brokerName
   *  alone is ambiguous across duplicate contacts. */
  brokerContactId?: string;
  /** Broker's parent agency accountid — deep-expanded from broker contact →
   *  parentcustomerid_account. */
  brokerAgencyId?: string;
  /** Broker's parent agency name — deep-expanded from broker contact →
   *  parentcustomerid_account. Empty when the broker is a free-floating
   *  contact or when no broker is set. */
  brokerAgencyName?: string;
  /** Agency parent accountid — the agency account's own parent
   *  (parentaccountid), one level above brokerAgency. */
  brokerAgencyParentId?: string;
  /** Agency parent name — e.g. a national aggregator (Acrisure, NFP) sitting
   *  above a local agency office. Empty when the agency has no parent. */
  brokerAgencyParentName?: string;
  createdOn?: string;
  modifiedOn?: string;
}

/**
 * Display-name policy (7/7/26 review): lists, titles, and search results show
 * the Dynamics account name. vba_name (the legal name) and otherGroupNames
 * stay search-matched but are not the primary display. Falls back to the
 * legal name for orphan groups with no linked account. Record-NAMING logic
 * (plan-year names etc.) intentionally keeps using `name`.
 */
export function groupDisplayName(g: Pick<OpsGroup, 'name' | 'accountName'>): string {
  return g.accountName?.trim() ? g.accountName : g.name;
}

export interface CreateOpsGroupInput {
  accountId: string;
  name: string;
  accountNumber?: string;
  city?: string;
  state?: string;
  isActive?: boolean;
  notes?: string;
  contractUrl?: string;
}

export interface UpdateOpsGroupInput {
  name?: string;
  accountNumber?: string;
  city?: string;
  state?: string;
  isActive?: boolean;
  termReason?: string;
  notes?: string;
  otherGroupNames?: string;
  contractUrl?: string;
  implementationPhase?: ImplementationPhaseValue;
  verified?: boolean;
  doNotContact?: boolean;
}

export interface OpsGroupQueryParams {
  activeOnly?: boolean;
  nameFilter?: string;
}
